/**
 * « Vie des projets » (maquette écran 4) — le feuillet `Data Scaleups` du PAD,
 * rendu depuis les jalons du hub : une ligne par projet, sa prochaine revue,
 * son échéance, sa conformité et l'avancement de ses fiches.
 *
 * Table MUI et non DataGrid : l'édition n'a pas lieu ici, et le groupement de
 * lignes n'existe pas en édition Community (règle MUI X du CLAUDE.md).
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/auth/useAuth';
import { colors } from '../core/theme/theme';
import { useProjectsHubData } from '../features/projects/useProjectsHubData';
import { useUsersList } from '../features/projects/useUsersList';
import { useAllMilestones } from '../features/projects/useAllMilestones';
import { buildProjectLifeRows, COMPLIANCE_COLOR } from '../features/projects/compliance';
import { formatDay } from '../features/projects/MilestoneRow';

type Filter = 'all' | 'ScaleUp' | 'LaunchPad' | 'late' | 'mine';

export default function ProjectLifePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');

  const { projects, clients, loading: projectsLoading, error } = useProjectsHubData();
  const { rows: milestones, loading: milestonesLoading } = useAllMilestones(true);
  const { users } = useUsersList(true);

  const now = useMemo(() => new Date(), []);
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.id, u.data.displayName);
    return m;
  }, [users]);

  const allRows = useMemo(
    () =>
      buildProjectLifeRows(
        projects,
        milestones,
        now,
      ),
    [projects, milestones, now],
  );

  const rows = useMemo(() => {
    switch (filter) {
      case 'all': return allRows;
      case 'late': return allRows.filter((r) => r.state === 'acceptable' || r.state === 'nok');
      case 'mine': return allRows.filter((r) => r.managerUids.includes(user?.uid ?? ''));
      default: return allRows.filter((r) => r.program === filter);
    }
  }, [allRows, filter, user?.uid]);

  const kpi = useMemo(() => {
    const count = (s: string) => allRows.filter((r) => r.state === s).length;
    const fvDone = allRows.reduce((n, r) => n + r.fvDone, 0);
    const fvTotal = allRows.reduce((n, r) => n + r.fvTotal, 0);
    return {
      ok: count('ok') + count('upcoming'),
      acceptable: count('acceptable'),
      nok: count('nok'),
      fvPct: fvTotal ? Math.round((fvDone / fvTotal) * 100) : 0,
    };
  }, [allRows]);

  if (error) return <Alert severity="error">{error.message}</Alert>;
  if (projectsLoading || milestonesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="project-life-page">
      <Typography variant="h2" sx={{ mb: 0.5 }}>
        {t('projectLife.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('projectLife.subtitle', { count: allRows.length })}
      </Typography>

      {/* KPI — carte sombre, même traitement que le bandeau de l'onglet Jalons. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          backgroundColor: colors.darkBg,
          color: colors.white,
          mb: 3,
        }}
      >
        <Kpi testId="project-life-kpi-ok" value={String(kpi.ok)} label={t('projectLife.kpiOk')} />
        <Kpi
          testId="project-life-kpi-acceptable"
          value={String(kpi.acceptable)}
          label={t('projectLife.kpiAcceptable')}
          color={colors.yellow}
        />
        <Kpi
          testId="project-life-kpi-nok"
          value={String(kpi.nok)}
          label={t('projectLife.kpiNok')}
          color={colors.red}
        />
        <Kpi
          testId="project-life-kpi-fv"
          value={`${kpi.fvPct}%`}
          label={t('projectLife.kpiFv')}
        />
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={filter}
        onChange={(_e, v: Filter | null) => v && setFilter(v)}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <ToggleButton value="all">{t('projectLife.filterAll')}</ToggleButton>
        <ToggleButton value="ScaleUp">{t('projects.program.ScaleUp')}</ToggleButton>
        <ToggleButton value="LaunchPad">{t('projects.program.LaunchPad')}</ToggleButton>
        <ToggleButton value="late">{t('projectLife.filterLate')}</ToggleButton>
        <ToggleButton value="mine">{t('projectLife.filterMine')}</ToggleButton>
      </ToggleButtonGroup>

      <TableContainer sx={{ border: `1px solid ${colors.lightGray}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.lightGray }}>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colProject')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colName')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colClient')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colManager')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colNextReview')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colDue')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colState')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('projectLife.colFv')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.projectId}
                hover
                data-testid={`project-life-row-${r.code}`}
                data-state={r.state}
                onClick={() => navigate(`/projects/${r.projectId}/milestones`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ fontWeight: 600 }}>{r.code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{clients.get(r.clientId)?.name ?? '—'}</TableCell>
                <TableCell>
                  {r.managerUids.map((uid) => nameOf.get(uid) ?? uid).join(', ') || '—'}
                </TableCell>
                <TableCell>{r.nextReview?.name ?? '—'}</TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {r.dueDate ? formatDay(r.dueDate) : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={COMPLIANCE_COLOR[r.state]}
                    label={
                      r.daysLate > 0
                        ? `${t(`projects.milestonesTab.state_${r.state}`)} · +${r.daysLate} j`
                        : t('projects.milestonesTab.stateUpcoming')
                    }
                  />
                </TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {r.fvTotal ? `${r.fvDone}/${r.fvTotal}` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Kpi({
  testId,
  value,
  label,
  color,
}: {
  testId: string;
  value: string;
  label: string;
  color?: string;
}) {
  return (
    <Box
      data-testid={testId}
      sx={{
        px: 2.25,
        py: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        borderRight: '1px solid #262A32',
        '&:last-of-type': { borderRight: 0 },
      }}
    >
      <Typography
        sx={{ fontSize: 32, fontWeight: 900, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#9AA0A8' }}>{label}</Typography>
    </Box>
  );
}
